import { formatAccountNumberForDisplay } from '@razz-team/utils';
import { useTranslation } from 'react-i18next';
import { ApiTransactionPart } from '../../../openapi';
import { ButtonLink, Stack, Typography } from '@razz-team/ui-web';
import { ReactNode } from 'react';

export function TransactionPartName({ party }: { party: ApiTransactionPart }): ReactNode {
  const { t } = useTranslation();
  if (party.beneficiary) {
    const routingNumber = party.beneficiary.routingNumber?.value;
    const accountNumber = (
      <Typography>{formatAccountNumberForDisplay(party.beneficiary.accountNumber)}</Typography>
    );
    return routingNumber ? (
      <Stack direction="column">
        <Typography>{routingNumber}</Typography>
        {accountNumber}
      </Stack>
    ) : (
      accountNumber
    );
  }

  if (party.payer) {
    switch (party.payer.type) {
      case 'ACQUIRING':
        return party.payer.name ?? t('Acquiring');
      case 'CRYPTO':
      case 'INDIVIDUAL':
        return party.payer.accountNumber.value;
      case 'OPEN_BANKING':
        return party.payer.name ?? t('Open Banking');
    }
  }

  if (party.card) {
    return (
      <ButtonLink variant="text" to="/account/cards/$cardId" params={{ cardId: party.card.cardId }}>
        {`${party.card.name ?? t('Card')} · ••${party.card.lastFour ?? ''}`}
      </ButtonLink>
    );
  }

  if (party.vault) {
    return (
      <ButtonLink
        variant="text"
        to="/account/vaults/$vaultId"
        params={{ vaultId: party.vault.vaultId }}
      >
        {t('Earning vault')}
      </ButtonLink>
    );
  }

  if (party.account) {
    return (
      <ButtonLink variant="text" to="/account/$accountId" params={{ accountId: party.account.id }}>
        {party.account.name ??
          t('{{currency}} Account', { currency: party.account.balance.currency })}
      </ButtonLink>
    );
  }

  if (party.customer) {
    return [party.customer.name, party.customer.username ? `@${party.customer.username}` : false]
      .filter(Boolean)
      .join(' ');
  }

  if (party.company) {
    return t('{{productName}}');
  }

  if (party.merchant) {
    return party.merchant.name;
  }

  if (party.atm) {
    return t('ATM');
  }

  if (party.acquiring) {
    return 'Acquiring';
  }

  return t('Unknown');
}
